import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminStats, getAdminGroups, updateGroupScanLimit, deleteGroup, downloadEmailsCsv, updatePdfExportGlobal, updateGroupPdfExport, compGroup, updateScanCostEstimate } from '../../services/adminService';

const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [pdfGlobalDefault, setPdfGlobalDefault] = useState(false);
  const [pdfGlobalSaving, setPdfGlobalSaving] = useState(false);
  const [pdfSavingId, setPdfSavingId] = useState(null);
  const [unpaidDaysInput, setUnpaidDaysInput] = useState('7');
  const [unpaidFilterDays, setUnpaidFilterDays] = useState(null); // null = filter off
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [costInput, setCostInput] = useState('');
  const [costSaving, setCostSaving] = useState(false);

  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      const blob = await downloadEmailsCsv();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cookbook-club-emails-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download emails CSV');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleSaveCost = async () => {
    const cents = Number(costInput);
    if (!Number.isFinite(cents) || cents < 0) {
      alert('Enter a valid, non-negative number of cents');
      return;
    }
    setCostSaving(true);
    try {
      await updateScanCostEstimate(cents);
      setEditingCost(false);
      const statsData = await getAdminStats();
      setStats(statsData.stats);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save cost estimate');
    } finally {
      setCostSaving(false);
    }
  };

  const loadGroups = async () => {
    const groupsData = await getAdminGroups();
    setGroups(groupsData.groups);
    setPdfGlobalDefault(groupsData.pdfGlobalDefault);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [statsData, groupsData] = await Promise.all([getAdminStats(), getAdminGroups()]);
        setStats(statsData.stats);
        setGroups(groupsData.groups);
        setPdfGlobalDefault(groupsData.pdfGlobalDefault);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleTogglePdfGlobal = async () => {
    setPdfGlobalSaving(true);
    try {
      const data = await updatePdfExportGlobal(!pdfGlobalDefault);
      setPdfGlobalDefault(data.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setPdfGlobalSaving(false);
    }
  };

  const handlePdfExportChange = async (groupId, selectValue) => {
    // selectValue: 'default' | 'on' | 'off'
    const value = selectValue === 'default' ? null : selectValue === 'on';
    setPdfSavingId(groupId);
    try {
      await updateGroupPdfExport(groupId, value);
      await loadGroups();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update PDF export setting');
    } finally {
      setPdfSavingId(null);
    }
  };

  const startEdit = (group) => {
    setEditingId(group.id);
    setEditValue(String(group.scan_limit ?? 10));
    setSavedId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (groupId) => {
    const scanLimit = Number(editValue);
    if (!Number.isInteger(scanLimit) || scanLimit < 0) {
      alert('Scan limit must be a non-negative whole number');
      return;
    }
    setSavingId(groupId);
    try {
      await updateGroupScanLimit(groupId, scanLimit);
      setEditingId(null);
      setSavedId(groupId);
      await loadGroups();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update scan limit');
    } finally {
      setSavingId(null);
    }
  };

  const handleComp = async (group) => {
    const choice = window.prompt(
      `Grant "${group.name}" free access. Type "starter" (5 members, 10 scans) or "community" (30 members, 20 scans):`,
      'community'
    );
    if (!choice) return;

    const planKey = choice.trim().toLowerCase();
    if (planKey !== 'starter' && planKey !== 'community') {
      alert('Please type exactly "starter" or "community"');
      return;
    }

    try {
      await compGroup(group.id, planKey);
      await loadGroups();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to grant free access');
    }
  };

  const handleDelete = async (group) => {
    const typed = window.prompt(
      `This permanently deletes "${group.name}" — all its recipes, photos, invites, and meal plans. This cannot be undone.\n\nType the group name exactly to confirm:`
    );
    if (typed !== group.name) {
      if (typed !== null) alert("Name didn't match — nothing was deleted.");
      return;
    }

    setDeletingId(group.id);
    try {
      await deleteGroup(group.id);
      await loadGroups();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete group');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p style={styles.status}>Loading admin dashboard...</p>;
  if (error) return <p style={styles.error}>{error}</p>;

  const filteredGroups = groups.filter((g) => {
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        g.name.toLowerCase().includes(q) ||
        g.owner_name.toLowerCase().includes(q) ||
        g.owner_email.toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }

    if (unpaidFilterDays !== null) {
      // A group still inside its free trial isn't "unpaid" in the abandoned sense --
      // only never-activated groups and groups whose trial has actually ended are.
      const inActiveTrial = g.status === 'trial' && !!g.trial_ends_at && new Date(g.trial_ends_at) > new Date();
      const isUnpaid = g.status !== 'active' && !inActiveTrial;
      const ageInDays = (Date.now() - new Date(g.created_at).getTime()) / (1000 * 60 * 60 * 24);
      if (!isUnpaid || ageInDays < unpaidFilterDays) return false;
    }

    return true;
  });

  const handleApplyUnpaidFilter = () => {
    const days = Number(unpaidDaysInput);
    if (!Number.isFinite(days) || days < 0) {
      alert('Enter a valid number of days');
      return;
    }
    setUnpaidFilterDays(days);
    setSelectedIds(new Set());
  };

  const handleClearUnpaidFilter = () => {
    setUnpaidFilterDays(null);
    setSelectedIds(new Set());
  };

  const toggleSelected = (groupId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredGroups.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredGroups.map((g) => g.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(
      `Permanently delete ${selectedIds.size} unpaid group(s)? This removes their recipes, invites, and photos. This cannot be undone.`
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    let failCount = 0;
    for (const groupId of selectedIds) {
      try {
        await deleteGroup(groupId);
      } catch (err) {
        failCount += 1;
      }
    }
    setBulkDeleting(false);
    setSelectedIds(new Set());
    await loadGroups();

    if (failCount > 0) {
      alert(`${failCount} group(s) failed to delete. The rest were removed successfully.`);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>🛠️ Admin Dashboard</h2>
        <div style={styles.headerLinks}>
          <Link to="/admin/pricing" style={styles.pricingLink}>Manage Pricing →</Link>
          <Link to="/admin/seed-photos" style={styles.pricingLink}>Manage Seed Photos →</Link>
          <Link to="/admin/marketing-gallery" style={styles.pricingLink}>Manage Screenshot Gallery →</Link>
          <Link to="/admin/group-setup-content" style={styles.pricingLink}>Edit "Create Cookbook" Page →</Link>
          <Link to="/admin/users-without-group" style={styles.pricingLink}>Users Without a Cookbook →</Link>
          <Link to="/admin/members-report" style={styles.pricingLink}>Group Members Report →</Link>
          <Link to="/admin/gift-codes" style={styles.pricingLink}>Gift Codes & Campaigns →</Link>
          <button onClick={handleDownloadCsv} style={styles.csvBtn} disabled={downloadingCsv}>
            {downloadingCsv ? 'Downloading...' : '⬇ Download Emails CSV'}
          </button>
        </div>
      </div>

      <div style={pdfGlobalDefault ? styles.pdfBannerActive : styles.pdfBanner}>
        <div>
          <p style={styles.pdfBannerTitle}>
            📕 Cookbook PDF Export — {pdfGlobalDefault ? 'ON by default for all groups' : 'OFF by default for all groups'}
          </p>
          <p style={styles.pdfBannerSubtitle}>
            Individual groups can still be overridden below, regardless of this global setting.
          </p>
        </div>
        <button onClick={handleTogglePdfGlobal} style={styles.pdfToggleBtn} disabled={pdfGlobalSaving}>
          {pdfGlobalSaving ? 'Saving...' : pdfGlobalDefault ? 'Turn Off for Everyone' : 'Turn On for Everyone'}
        </button>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.totalGroups}</span>
          <span style={styles.statLabel}>Groups</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.totalUsers}</span>
          <span style={styles.statLabel}>Users</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.totalRecipes}</span>
          <span style={styles.statLabel}>Recipes</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.activeSubscriptions}</span>
          <span style={styles.statLabel}>Paid Groups</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.compedGroups}</span>
          <span style={styles.statLabel}>Comped Groups</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{formatPrice(stats.totalRevenueCents)}</span>
          <span style={styles.statLabel}>Revenue</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{stats.totalScansUsed}</span>
          <span style={styles.statLabel}>Total Scans Used</span>
        </div>
        <div style={styles.statCardCost}>
          <span style={styles.statNumber}>{formatPrice(stats.estimatedScanCostCents)}</span>
          <span style={styles.statLabel}>Est. Scan Cost</span>
          {editingCost ? (
            <div style={styles.costEditRow}>
              <input
                type="number"
                min="0"
                step="0.1"
                value={costInput}
                onChange={(e) => setCostInput(e.target.value)}
                style={styles.costInput}
                autoFocus
              />
              <span style={styles.costUnit}>¢/scan</span>
              <button onClick={handleSaveCost} style={styles.costSaveBtn} disabled={costSaving}>
                {costSaving ? '...' : '✓'}
              </button>
              <button onClick={() => setEditingCost(false)} style={styles.costCancelBtn}>✕</button>
            </div>
          ) : (
            <span
              style={styles.costEditLink}
              onClick={() => { setCostInput(String(stats.costPerScanCents)); setEditingCost(true); }}
            >
              (assumes {stats.costPerScanCents}¢/scan — edit)
            </span>
          )}
        </div>
      </div>

      <div style={styles.tableCard}>
        <h3 style={styles.tableTitle}>Groups</h3>
        <p style={styles.settingsHint}>
          Click a group's scan limit to edit it individually. Each group's limit is set independently — there's no global default.
        </p>
        <input
          type="text"
          placeholder="Search by group name, owner name, or owner email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />

        <div style={styles.unpaidFilterRow}>
          <span style={styles.unpaidFilterLabel}>Show unpaid groups older than</span>
          <input
            type="number"
            min="0"
            value={unpaidDaysInput}
            onChange={(e) => setUnpaidDaysInput(e.target.value)}
            style={styles.unpaidDaysInput}
          />
          <span style={styles.unpaidFilterLabel}>days</span>
          <button onClick={handleApplyUnpaidFilter} style={styles.unpaidApplyBtn}>Show</button>
          {unpaidFilterDays !== null && (
            <button onClick={handleClearUnpaidFilter} style={styles.unpaidClearBtn}>Clear Filter</button>
          )}
          {unpaidFilterDays !== null && selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} style={styles.bulkDeleteBtn} disabled={bulkDeleting}>
              {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
            </button>
          )}
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {unpaidFilterDays !== null && (
                  <th style={styles.th}>
                    <input
                      type="checkbox"
                      checked={filteredGroups.length > 0 && selectedIds.size === filteredGroups.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Owner</th>
                <th style={styles.th}>Plan</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Members</th>
                <th style={styles.th}>Recipes</th>
                <th style={styles.th}>Scan Limit</th>
                <th style={styles.th}>PDF Export</th>
                <th style={styles.th}>Last Active</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map((g) => (
                <tr key={g.id}>
                  {unpaidFilterDays !== null && (
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(g.id)}
                        onChange={() => toggleSelected(g.id)}
                      />
                    </td>
                  )}
                  <td style={styles.td}>{g.name}</td>
                  <td style={styles.td}>{g.owner_name}<br /><span style={styles.subtle}>{g.owner_email}</span></td>
                  <td style={styles.td}>{g.plan || '—'}</td>
                  <td style={styles.td}>
                    <span style={g.status === 'active' ? styles.statusActive : styles.statusInactive}>
                      {g.status === 'trial' && g.trial_ends_at
                        ? (new Date(g.trial_ends_at) > new Date()
                            ? `trial (${Math.ceil((new Date(g.trial_ends_at) - new Date()) / 86400000)}d left)`
                            : 'trial ended')
                        : (g.status || 'inactive')}
                    </span>
                  </td>
                  <td style={styles.td}>{g.member_count}{g.max_members ? ` / ${g.max_members}` : ''}</td>
                  <td style={styles.td}>{g.recipe_count}</td>
                  <td style={styles.td}>
                    {editingId === g.id ? (
                      <div style={styles.editRow}>
                        <input
                          type="number"
                          min="0"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          style={styles.editInput}
                          autoFocus
                        />
                        <button onClick={() => saveEdit(g.id)} style={styles.editSaveBtn} disabled={savingId === g.id}>
                          {savingId === g.id ? '...' : '✓'}
                        </button>
                        <button onClick={cancelEdit} style={styles.editCancelBtn}>✕</button>
                      </div>
                    ) : (
                      <span style={styles.scanLimitDisplay} onClick={() => startEdit(g)} title="Click to edit">
                        {g.scan_count ?? 0} / {g.scan_limit ?? 10} ✏️
                        {savedId === g.id && <span style={styles.savedTag}> Saved</span>}
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <select
                      value={g.pdf_export_enabled === null || g.pdf_export_enabled === undefined ? 'default' : (g.pdf_export_enabled ? 'on' : 'off')}
                      onChange={(e) => handlePdfExportChange(g.id, e.target.value)}
                      style={styles.pdfSelect}
                      disabled={pdfSavingId === g.id}
                    >
                      <option value="default">Default ({pdfGlobalDefault ? 'On' : 'Off'})</option>
                      <option value="on">Force On</option>
                      <option value="off">Force Off</option>
                    </select>
                  </td>
                  <td style={styles.td}>
                    {g.last_active
                      ? new Date(g.last_active).toLocaleDateString()
                      : <span style={styles.subtle}>Never</span>}
                  </td>
                  <td style={styles.td}>{new Date(g.created_at).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    <div style={styles.actionsCell}>
                      <Link to={`/admin/groups/${g.id}/recipes`} style={styles.viewRecipesBtn}>
                        👁️ View
                      </Link>
                      <button onClick={() => handleComp(g)} style={styles.compBtn}>
                        🎁 Comp
                      </button>
                      <button
                        onClick={() => handleDelete(g)}
                        style={styles.deleteBtn}
                        disabled={deletingId === g.id}
                      >
                        {deletingId === g.id ? '...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredGroups.length === 0 && <p style={styles.status}>{searchQuery ? 'No groups match your search' : 'No groups yet'}</p>}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {},
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { color: '#333', margin: 0 },
  pricingLink: { color: '#e67e22', fontWeight: '600', fontSize: '14px', textDecoration: 'none' },
  headerLinks: { display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' },
  csvBtn: {
    padding: '8px 14px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  pdfBanner: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
    padding: '16px 20px', borderRadius: '10px', marginBottom: '20px',
    backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0',
  },
  pdfBannerActive: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
    padding: '16px 20px', borderRadius: '10px', marginBottom: '20px',
    backgroundColor: '#eafaf1', border: '1px solid #b8e6ca',
  },
  pdfBannerTitle: { margin: '0 0 4px 0', fontWeight: '700', color: '#333', fontSize: '14px' },
  pdfBannerSubtitle: { margin: 0, color: '#777', fontSize: '12px', maxWidth: '480px' },
  pdfToggleBtn: {
    padding: '10px 18px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '13px',
  },
  pdfSelect: { padding: '4px 6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '14px', marginBottom: '28px' },
  statCard: {
    backgroundColor: 'white', borderRadius: '10px', padding: '20px', textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '4px',
  },
  statNumber: { fontSize: '26px', fontWeight: '700', color: '#e67e22' },
  statLabel: { fontSize: '12px', color: '#888', textTransform: 'uppercase', fontWeight: '600' },
  statCardCost: {
    backgroundColor: 'white', borderRadius: '10px', padding: '20px', textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '4px',
  },
  costEditLink: { fontSize: '10px', color: '#bbb', cursor: 'pointer', marginTop: '2px' },
  costEditRow: { display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center', marginTop: '4px' },
  costInput: { width: '50px', padding: '3px 5px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '12px' },
  costUnit: { fontSize: '10px', color: '#999' },
  costSaveBtn: { padding: '3px 7px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' },
  costCancelBtn: { padding: '3px 7px', backgroundColor: '#f5f5f5', color: '#999', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' },
  tableCard: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
  settingsHint: { color: '#888', fontSize: '12px', marginBottom: '16px' },
  searchInput: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box',
  },
  unpaidFilterRow: {
    display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap',
    padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #eee',
  },
  unpaidFilterLabel: { fontSize: '13px', color: '#666' },
  unpaidDaysInput: { width: '60px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' },
  unpaidApplyBtn: {
    padding: '6px 14px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  unpaidClearBtn: {
    padding: '6px 14px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd',
    borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
  },
  bulkDeleteBtn: {
    padding: '6px 14px', backgroundColor: '#e74c3c', color: 'white', border: 'none',
    borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', marginLeft: 'auto',
  },
  tableTitle: { color: '#333', marginBottom: '8px' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '10px', borderBottom: '2px solid #f0f0f0', color: '#888', fontSize: '11px', textTransform: 'uppercase' },
  td: { padding: '10px', borderBottom: '1px solid #f5f5f5', color: '#444' },
  subtle: { color: '#aaa', fontSize: '11px' },
  statusActive: { color: '#27ae60', fontWeight: '600', backgroundColor: '#eafaf1', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' },
  statusInactive: { color: '#e67e22', fontWeight: '600', backgroundColor: '#fdf0e3', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' },
  scanLimitDisplay: { cursor: 'pointer', color: '#444' },
  editRow: { display: 'flex', alignItems: 'center', gap: '4px' },
  editInput: { width: '60px', padding: '4px 6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' },
  editSaveBtn: { padding: '4px 8px', backgroundColor: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  editCancelBtn: { padding: '4px 8px', backgroundColor: '#f5f5f5', color: '#999', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  savedTag: { color: '#27ae60', fontSize: '11px', marginLeft: '6px' },
  actionsCell: { display: 'flex', gap: '6px' },
  viewRecipesBtn: {
    padding: '6px 10px', backgroundColor: '#f5f5f5', color: '#555', border: '1px solid #ddd',
    borderRadius: '6px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', textDecoration: 'none',
  },
  compBtn: {
    padding: '6px 10px', backgroundColor: '#fdf0e3', color: '#e67e22',
    border: '1px solid #e67e22', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap',
  },
  deleteBtn: {
    padding: '6px 12px', backgroundColor: 'transparent', color: '#e74c3c',
    border: '1px solid #e74c3c', borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
  },
  status: { textAlign: 'center', color: '#999' },
  error: { textAlign: 'center', color: '#e74c3c' },
};

export default AdminDashboard;
